<template>
  <div class="forma-erp kasa-racuni">

    <div class="traka-ekrana">
      <span class="naslov">Računi s kase</span>
      <span class="broj" v-if="cekaju > 0">{{ cekaju }} čeka JIR</span>
      <span class="uz">{{ nazivFirme }} · {{ $store.state.businessYear }}</span>
      <v-spacer></v-spacer>
      <v-btn text small color="primary" class="text-none" @click="natrag">
        <v-icon small left>mdi-cash-register</v-icon>Natrag na kasu
      </v-btn>
    </div>

    <!-- Kartica bez poznatog ishoda je gore od isteklog roka: ne zna se je li
         novac uzet, pa se takav racun ne smije fiskalizirati napamet. -->
    <div class="upozorenje kartica" v-if="neizvjesne > 0">
      <v-icon color="red darken-2">mdi-credit-card-off-outline</v-icon>
      <div>
        <b>{{ neizvjesne }} {{ neizvjesne === 1 ? 'račun ima' : 'računa ima' }} karticu bez poznatog ishoda.</b>
        <span class="uz">
          Provjerite na aparatu je li plaćanje prošlo prije nego ih pošaljete Poreznoj.
        </span>
      </div>
    </div>

    <!-- Rok od 48 sati je jedino sto ovdje stvarno gori, pa stoji na vrhu. -->
    <div class="upozorenje" v-if="istekli > 0">
      <v-icon color="orange darken-3">mdi-clock-alert-outline</v-icon>
      <div>
        <b>{{ istekli }} {{ istekli === 1 ? 'račun je stariji' : 'računa su starija' }} od 48 sati.</b>
        <span class="uz">
          Pri slanju će im se datum i vrijeme prepisati na tekuće — tako nalaže rok.
        </span>
      </div>
    </div>

    <div class="pa-3">
      <v-card outlined>
        <v-card-title class="naslov-kartice">
          {{ samoNefiskalizirani ? 'Čekaju fiskalizaciju' : 'Svi računi' }}
          <span class="dodatak">{{ racuni.length }} {{ mnozina(racuni.length) }}</span>
          <v-spacer></v-spacer>
          <v-btn text small color="primary" class="text-none" @click="prebaciPrikaz">
            {{ samoNefiskalizirani ? 'Prikaži sve' : 'Samo one bez JIR-a' }}
          </v-btn>
        </v-card-title>

        <div class="alatke">
          <v-btn small color="primary" class="text-none"
            :disabled="!zaSlanje.length || saljem" :loading="saljem"
            @click="posaljiSve">
            Pošalji sve ({{ zaSlanje.length }})
          </v-btn>
          <v-btn small outlined color="primary" class="text-none"
            :disabled="saljem" @click="ucitaj">Osvježi</v-btn>
          <span class="uz" v-if="saljem">
            Šaljem {{ poslano }} / {{ ukupnoZaSlanje }}…
          </span>
          <v-spacer></v-spacer>
          <span class="uz" v-if="greskaPopisa">{{ greskaPopisa }}</span>
        </div>

        <v-data-table
          dense
          :headers="stupci"
          :items="racuni"
          :loading="ucitavam"
          :items-per-page="50"
          :footer-props="{'items-per-page-options': [25, 50, 100, 200]}"
          item-key="id"
          :mobile-breakpoint="0"
        >
          <template v-slot:[`item.brojRacuna`]="{ item }">
            <span class="broj-racuna">{{ item.brojRacuna }}</span>
          </template>

          <template v-slot:[`item.datum`]="{ item }">
            {{ datum(item.datum) }} <span class="uz">{{ (item.vrijeme || '').slice(0, 5) }}</span>
          </template>

          <template v-slot:[`item.kupac`]="{ item }">
            <span v-if="item.kupac">
              {{ item.kupac }}
              <span class="uz" v-if="item.kupacOib">· {{ item.kupacOib }}</span>
            </span>
            <span v-if="!item.kupac" class="uz">bez kupca</span>
          </template>

          <!-- Nacin placanja stoji pod iznosom, a ne u svom stupcu: s njim je
               tablica bila sira od kartice i zadnji stupac se rezao. -->
          <template v-slot:[`item.iznos`]="{ item }">
            <div class="iznos">{{ novac(item.iznos) }}</div>
            <div class="uz">{{ nazivPlacanja(item.placanje) }}</div>
          </template>

          <template v-slot:[`item.cekao`]="{ item }">
            <span v-if="item.satiOdIzdavanja === null" class="uz">—</span>
            <span v-if="item.satiOdIzdavanja !== null"
              :class="item.istekaoRok && !item.jir ? 'isteklo' : ''">
              {{ cekanje(item.satiOdIzdavanja) }}
            </span>
          </template>

          <!-- JIR stoji ovdje, pod oznakom stanja, a ne u stupcu s tipkom:
               36 znakova ondje nije stalo pa je tablica bjezala u stranu. -->
          <template v-slot:[`item.stanje`]="{ item }">
            <span class="znak" :class="znakKlasa(item)">{{ znakTekst(item) }}</span>
            <div class="uz greska" v-if="!item.jir && item.greska">{{ item.greska }}</div>
            <div class="uz jir" v-if="item.jir" :title="item.jir">{{ item.jir }}</div>
            <div class="uz kartica" v-if="item.kartica" :class="{lose: !item.kartica.uspjelo}">
              Kartica: {{ opisKartice(item.kartica) }}
            </div>
          </template>

          <template v-slot:[`item.akcija`]="{ item }">
            <v-btn small outlined color="primary" class="text-none mr-1"
              v-if="!item.jir" :disabled="saljem" @click="posalji(item)">
              Pošalji
            </v-btn>
            <v-btn icon small :disabled="ispisujem" title="Ispiši kopiju na traku"
              @click="ispisiKopiju(item)">
              <v-icon small color="grey darken-1">mdi-printer</v-icon>
            </v-btn>
            <v-btn icon small title="Otvori kao A4" @click="otvoriKaoA4(item)">
              <v-icon small color="grey darken-1">mdi-file-document-outline</v-icon>
            </v-btn>
            <!-- Brisanje samo dok racun nema JIR. Fiskaliziran racun Porezna
                 vec ima, pa se ne smije izgubiti kod nas. -->
            <v-btn icon small v-if="!item.jir" :disabled="brisem"
              title="Obriši račun" @click="pitajBrisanje(item)">
              <v-icon small color="grey darken-1">mdi-delete-outline</v-icon>
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="prazno">
              {{ samoNefiskalizirani
                ? 'Nema računa koji čekaju fiskalizaciju.'
                : 'Nema računa.' }}
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="pitam" max-width="480" content-class="kasa-racuni">
      <v-card v-if="zaBrisanje">
        <v-card-title class="naslov-kartice">Obrisati račun?</v-card-title>
        <v-card-text class="pt-4">
          <div class="zavrseno">
            <span>{{ zaBrisanje.brojRacuna }}</span>
            <b>{{ novac(zaBrisanje.iznos) }}</b>
          </div>
          <div class="uz mt-1">
            {{ datum(zaBrisanje.datum) }} {{ (zaBrisanje.vrijeme || '').slice(0, 5) }}
            <span v-if="zaBrisanje.kupac">· {{ zaBrisanje.kupac }}</span>
          </div>

          <!-- Zasto se pita dvaput: brojevi racuna moraju teci bez rupa.
               Ako nijedan racun iznad nije fiskaliziran, svi se pomaknu za
               jedan unatrag i rupe nema. Ako je medu njima fiskaliziran, njemu
               se broj ne smije dirati - tada rupa ostaje i to se trazi
               izricito. -->
          <v-alert v-if="!upozorenjeRupa" type="info" dense text class="mt-4 mb-0">
            Računi iznad ovoga pomaknut će se za jedan unatrag, pa u brojevima
            neće ostati rupa.
          </v-alert>
          <v-alert v-if="upozorenjeRupa" type="warning" dense text class="mt-4 mb-0">
            {{ upozorenjeRupa }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="pitam = false">Odustani</v-btn>
          <v-btn :color="upozorenjeRupa ? 'warning' : 'error'" :loading="brisem"
            @click="obrisi">
            {{ upozorenjeRupa ? 'Svejedno obriši' : 'Obriši' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <KasaA4Pregled ref="a4" />

    <v-snackbar v-model="poruka" :color="bojaPoruke" :timeout="5000" bottom>
      {{ tekstPoruke }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="poruka = false">Zatvori</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import KasaService from '@/services/KasaService'
import KasaA4Pregled from '@/components/Kasa/KasaA4Pregled'
import moneyFormat from '@/snippets/moneyFormat'
import dateFormat from '@/snippets/dateFormat'
import { sendToPrinter, printServiceUrl, printerName } from '@/components/Document/posPrint'


// Poruka o pomaku se cita nakon svakog brisanja, pa neka bude napisana kako
// se govori: "1 račun je dobio", "3 računa su dobila".
const recenicaPomaka = (n) => (n === 1
  ? '1 račun je dobio novi broj.'
  : `${n} računa su dobila nove brojeve.`)

export default {
  name: 'kasaracuni',
  components: {KasaA4Pregled},
  data () {
    return {
      racuni: [],
      cekaju: 0,
      istekli: 0,
      neizvjesne: 0,
      samoNefiskalizirani: true,
      ucitavam: false,
      saljem: false,
      ispisujem: false,
      brisem: false,
      pitam: false,
      zaBrisanje: null,
      upozorenjeRupa: '',
      poslano: 0,
      ukupnoZaSlanje: 0,
      greskaPopisa: '',
      poruka: false,
      tekstPoruke: '',
      bojaPoruke: 'success'
    }
  },

  computed: {
    nazivFirme () {
      return this.$store.state.companyName || ''
    },
    stupci () {
      return [
        {text: 'Broj', value: 'brojRacuna', width: '105px'},
        {text: 'Datum', value: 'datum', width: '140px'},
        {text: 'Kupac', value: 'kupac'},
        {text: 'Iznos', value: 'iznos', align: 'end', width: '110px'},
        {text: 'Čeka', value: 'cekao', width: '80px'},
        {text: 'Stanje', value: 'stanje', width: '230px'},
        {text: '', value: 'akcija', sortable: false, width: '140px'}
      ]
    },
    zaSlanje () {
      return this.racuni.filter(r => !r.jir)
    }
  },

  async mounted () {
    await this.ucitaj()
  },

  methods: {
    novac (v) { return moneyFormat(v) },
    datum (v) { return dateFormat(v) },

    mnozina (n) {
      if (n === 1) { return 'račun' }
      if (n >= 2 && n <= 4) { return 'računa' }
      return 'računa'
    },

    nazivPlacanja (kod) {
      return {G: 'Gotovina', K: 'Kartica', T: 'Transakcijski', O: 'Ostalo'}[kod] || kod || '—'
    },

    // Sati se citaju teze od "3 dana", a upravo o tome ovisi rok.
    cekanje (sati) {
      if (sati < 1) { return Math.max(1, Math.round(sati * 60)) + ' min' }
      if (sati < 48) { return Math.round(sati) + ' h' }
      return Math.floor(sati / 24) + ' d'
    },

    // Ishod na aparatu, kratko. "Nepoznato" je stanje koje trazi provjeru, ne
    // greska - aparat je mozda dovrsio nakon sto je kasa odustala.
    opisKartice (k) {
      if (!k) { return '' }
      if (k.uspjelo) { return 'odobrena' }
      if (!k.gotovo) { return 'nepoznato — provjerite aparat' }
      return k.poruka || 'nije prošla'
    },

    znakTekst (r) {
      if (r.jir) { return r.naknadnaDostava ? 'Fiskaliziran (naknadno)' : 'Fiskaliziran' }
      if (r.fisStatus === 'greska') { return 'Greška pri slanju' }
      return 'Čeka JIR'
    },
    znakKlasa (r) {
      if (r.jir) { return 'ok' }
      if (r.fisStatus === 'greska') { return 'lose' }
      return 'ceka'
    },

    async ucitaj () {
      this.ucitavam = true
      this.greskaPopisa = ''
      try {
        const {data} = await KasaService.racuni({
          CompanyId: this.$store.state.companyid,
          BusinessYear: this.$store.state.businessYear,
          sve: this.samoNefiskalizirani ? 'false' : 'true'
        })
        this.racuni = data.racuni || []
        this.cekaju = data.cekaju || 0
        this.istekli = data.istekli || 0
        this.neizvjesne = data.neizvjesne || 0
      } catch (err) {
        this.greskaPopisa = this.tekstGreske(err)
        this.javi(this.greskaPopisa, 'red')
      } finally {
        this.ucitavam = false
      }
    },

    async prebaciPrikaz () {
      this.samoNefiskalizirani = !this.samoNefiskalizirani
      await this.ucitaj()
    },

    async posalji (racun) {
      this.saljem = true
      try {
        // false = racun je kupac vec dobio, ide kao naknadna dostava
        const {data} = await KasaService.fiskaliziraj(racun.id, false)
        if (data.poslano) {
          this.javi(`Račun ${racun.brojRacuna} je fiskaliziran.`, 'green')
        } else {
          this.javi(`Račun ${racun.brojRacuna}: ${data.greska}`, 'orange darken-2')
        }
      } catch (err) {
        this.javi(this.tekstGreske(err), 'red')
      } finally {
        this.saljem = false
        await this.ucitaj()
      }
    },

    // Jedan po jedan, a ne svi odjednom: svaki racun ide Poreznoj i traje,
    // a ako servis ne radi nema smisla nastaviti kroz cijeli popis.
    async posaljiSve () {
      const popis = this.zaSlanje.slice()
      if (!popis.length) { return }
      this.saljem = true
      this.poslano = 0
      this.ukupnoZaSlanje = popis.length
      let uspjelo = 0
      let palo = 0
      let zadnjaGreska = ''

      try {
        for (const r of popis) {
          try {
            const {data} = await KasaService.fiskaliziraj(r.id, false)
            if (data.poslano) { uspjelo++ } else { palo++; zadnjaGreska = data.greska || '' }
          } catch (err) {
            palo++
            zadnjaGreska = this.tekstGreske(err)
          }
          this.poslano++
          // Tri uzastopna neuspjeha znace da servis ne radi - nema smisla
          // gnjaviti Poreznu ostatkom popisa.
          if (palo >= 3 && uspjelo === 0) {
            this.javi(`Prekinuto nakon ${palo} neuspjeha: ${zadnjaGreska}`, 'red')
            break
          }
        }
        if (uspjelo > 0) {
          this.javi(`Fiskalizirano ${uspjelo} od ${popis.length}.` +
            (palo ? ` Nije prošlo ${palo}.` : ''), palo ? 'orange darken-2' : 'green')
        } else if (palo && palo < 3) {
          this.javi(`Nijedan račun nije prošao: ${zadnjaGreska}`, 'red')
        }
      } finally {
        this.saljem = false
        this.poslano = 0
        this.ukupnoZaSlanje = 0
        await this.ucitaj()
      }
    },

    // A4 s podacima za uplatu i 2D kodom - ono sto kupac dobije uz placanje
    // transakcijskim racunom.
    async otvoriKaoA4 (racun) {
      try {
        const {data} = await KasaService.ispis(racun.id, {kopija: true})
        if (this.$refs.a4) { this.$refs.a4.prikazi(data.ispis) }
      } catch (err) {
        this.javi(this.tekstGreske(err), 'red')
      }
    },

    // Kopija se gradi iz spremljenog racuna, pa je ista kao original bez
    // obzira koliko je vremena proslo. Pisac je uz kasu, na localhostu ovog
    // racunala - zato ga zove preglednik.
    pitajBrisanje (racun) {
      this.zaBrisanje = racun
      this.upozorenjeRupa = ''
      this.pitam = true
    },

    // Prvi poziv ide bez potvrde. Ako posluzitelj javi da racun nije zadnji u
    // nizu, pitanje se ponavlja s objasnjenjem - rupa u brojevima ne smije
    // nastati usput.
    async obrisi () {
      if (!this.zaBrisanje) { return }
      this.brisem = true
      try {
        const {data} = await KasaService.obrisi(this.zaBrisanje.id, !!this.upozorenjeRupa)
        this.pitam = false
        const pomak = data.pomaknuto ? ' ' + recenicaPomaka(data.pomaknuto) : ''
        this.javi(
          `Račun ${this.zaBrisanje.brojRacuna} je obrisan.` +
          (data.ostalaRupa ? ' U brojevima je ostala rupa.' : pomak),
          data.ostalaRupa ? 'orange darken-2' : 'green')
        this.zaBrisanje = null
        this.upozorenjeRupa = ''
        await this.ucitaj()
      } catch (err) {
        const t = err && err.response && err.response.data ? err.response.data : {}
        if (t.razlog === 'rupa') {
          this.upozorenjeRupa = t.greska
        } else {
          this.pitam = false
          this.javi(this.tekstGreske(err), 'red')
        }
      } finally {
        this.brisem = false
      }
    },

    async ispisiKopiju (racun) {
      this.ispisujem = true
      try {
        const {data} = await KasaService.ispis(racun.id, {kopija: true})
        const pisac = printerName()
        if (pisac) { data.ispis.printer = pisac }
        const odgovor = await sendToPrinter(data.ispis)
        if (odgovor.ok) {
          this.javi(`Kopija računa ${racun.brojRacuna} poslana na pisač.`, 'green')
        } else {
          this.javi(`Pisač nije odgovorio (${printServiceUrl()}): ` +
            (typeof odgovor.body === 'string' ? odgovor.body : JSON.stringify(odgovor.body)), 'red')
        }
      } catch (err) {
        this.javi(this.tekstGreske(err), 'red')
      } finally {
        this.ispisujem = false
      }
    },

    natrag () {
      this.$router.push({name: 'kasa'})
    },

    tekstGreske (err) {
      const t = err && err.response && err.response.data ? err.response.data : null
      if (t && t.greska) { return t.greska }
      return err && err.message ? err.message : 'Nepoznata greška.'
    },

    javi (tekst, boja) {
      this.tekstPoruke = tekst
      this.bojaPoruke = boja || 'success'
      this.poruka = true
    }
  }
}
</script>

<style scoped>
.kasa-racuni { background: #eceff1; min-height: calc(100vh - 64px); }
.kasa-racuni .traka-ekrana { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

.kasa-racuni .upozorenje {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.kasa-racuni .upozorenje b { color: #8d6e00; }
.kasa-racuni .upozorenje .uz { color: #8d6e00; margin-left: 6px; font-weight: 400; }

/* Kartica bez poznatog ishoda je teza od isteklog roka - crveno, ne zuto. */
.kasa-racuni .upozorenje.kartica { background: #ffebee; border-left-color: #c62828; }
.kasa-racuni .upozorenje.kartica b,
.kasa-racuni .upozorenje.kartica .uz { color: #b71c1c; }

.kasa-racuni .uz.kartica { color: #2e7d32; white-space: normal; line-height: 1.3; }
.kasa-racuni .uz.kartica.lose { color: #c62828; }

.kasa-racuni .alatke {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.kasa-racuni .uz { color: #607d8b; font-size: 12px; }
.kasa-racuni .broj-racuna { font-weight: 600; font-variant-numeric: tabular-nums; }
.kasa-racuni .iznos { font-weight: 700; font-variant-numeric: tabular-nums; }
.kasa-racuni .jir { font-family: monospace; font-size: 11px; white-space: normal; word-break: break-all; }
.kasa-racuni .greska { color: #c62828; white-space: normal; line-height: 1.3; }
.kasa-racuni .isteklo { color: #e65100; font-weight: 700; }
.kasa-racuni .zavrseno { display: flex; justify-content: space-between; align-items: baseline; }
.kasa-racuni .zavrseno span { font-weight: 600; font-variant-numeric: tabular-nums; }
.kasa-racuni .zavrseno b { font-size: 22px; font-variant-numeric: tabular-nums; }
.kasa-racuni .naslov-kartice { font-size: 17px; font-weight: 700; color: #1565c0;
  background: #eef4fb; border-bottom: 2px solid #1976d2; padding: 8px 14px; min-height: 0; }
.kasa-racuni .prazno { padding: 40px 16px; text-align: center; color: #90a4ae; }

.kasa-racuni .znak {
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.kasa-racuni .znak.ok { background: #e8f5e9; color: #2e7d32; }
.kasa-racuni .znak.ceka { background: #fff8e1; color: #8d6e00; }
.kasa-racuni .znak.lose { background: #fdecea; color: #c62828; }

.kasa-racuni >>> td, .kasa-racuni >>> th { white-space: nowrap; }
.kasa-racuni >>> td .greska { white-space: normal; }
.kasa-racuni .naslov-kartice .dodatak { font-weight: 400; font-size: 12px; color: #6b8299; margin-left: 8px; }
</style>
